import {Button, H4, Icon, TextRegular} from '@hm/fundamental'
import {mdiPin, mdiPinOutline, mdiPlus} from '@mdi/js'
import {useMemo, useRef, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import type {ExtendedGlobalMeasurementType} from '../../types/GlobalMeasurementType'
import type {LimsMeasurementType} from '../../types/LimsMeasurementType'

import {useManageMappingRows, useManageUnsavedMappingRows} from './hooks'
import {MappingRow} from './MappingRow'
import {UnsavedMappingRow} from './UnsavedMappingRow'

export interface LimsMeasurementTypesFormProps {
  globalMeasurementType: ExtendedGlobalMeasurementType
  unmappedLimsMeasurementTypes: LimsMeasurementType[]
  canChangeMappings: boolean
  plantCode: string
  onPinGlobalMeasurementType: (globalMeasurementTypeId: number) => void
  isPinned: boolean
  isPinPending: boolean
}

export const LimsMeasurementTypesForm: React.FC<LimsMeasurementTypesFormProps> = ({
  globalMeasurementType,
  unmappedLimsMeasurementTypes,
  canChangeMappings,
  plantCode,
  onPinGlobalMeasurementType,
  isPinned,
  isPinPending
}) => {
  const {t} = useTranslation()

  const rowsOrderRef = useRef<number[]>([])

  const {
    id: globalTypeId,
    name: globalTypeName,
    description: globalTypeDescription,
    unit: globalTypeUnit,
    limsMeasurementTypes,
    measurementQuantity
  } = globalMeasurementType

  const sortedLimsMeasurementTypes = useMemo(() => {
    const order = rowsOrderRef.current
    return [...limsMeasurementTypes].sort((a, b) => {
      const indexA = order.indexOf(a.id)
      const indexB = order.indexOf(b.id)
      const safeIndexA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA
      const safeIndexB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB
      return safeIndexA - safeIndexB
    })
  }, [limsMeasurementTypes])

  const units = measurementQuantity?.units ?? []

  const {
    isMeasurementTypeBeingMapped,
    isMeasurementTypeBeingUnmapped,
    handleSelectLimsType,
    handleSelectLimsUnit,
    handleDeleteMappingRow
  } = useManageMappingRows({
    plantCode,
    rowsOrderRef,
    sortedLimsMeasurementTypes,
    globalTypeId
  })

  const {
    unsavedRows,
    isUnsavedRowBeingMapped,
    handleAddMappingRow,
    handleDeleteUnsavedMappingRow,
    handleSelectTypeInUnsavedMappingRow
  } = useManageUnsavedMappingRows({
    plantCode,
    unmappedLimsMeasurementTypes,
    globalTypeId
  })

  const isFormDisabled =
    !canChangeMappings ||
    isUnsavedRowBeingMapped ||
    isMeasurementTypeBeingMapped ||
    isMeasurementTypeBeingUnmapped

  const testIdPrefix = `measurement_type_${globalTypeName.replace(/\s+/g, '_')}`

  const hasAnyRows = sortedLimsMeasurementTypes.length > 0 || unsavedRows.length > 0

  useEffect(() => {
    const mappedLimsIds = limsMeasurementTypes.map((m) => m.id)
    const existingOrder = rowsOrderRef.current

    const newMappedLimsIds = mappedLimsIds.filter((id) => !existingOrder.includes(id))

    if (newMappedLimsIds.length > 0) {
      rowsOrderRef.current = [...existingOrder, ...newMappedLimsIds]
    }
  }, [limsMeasurementTypes])

  return (
    <div data-test-id={testIdPrefix}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div data-test-id={`${testIdPrefix}_info`}>
          <H4 data-test-id={`${testIdPrefix}_info_title`}>
            {`${globalTypeName}${globalTypeUnit ? ` (${globalTypeUnit})` : ''}`}
          </H4>
          {globalTypeDescription ? (
            <TextRegular
              className="text-sm text-foreground_faded"
              data-test-id={`${testIdPrefix}_info_caption`}
            >
              {globalTypeDescription}
            </TextRegular>
          ) : null}
        </div>
        <div className="mb-4 flex items-start justify-between gap-2">
          <Button
            variant="tertiary"
            disabled={isFormDisabled || unmappedLimsMeasurementTypes.length === 0}
            onClick={handleAddMappingRow}
            data-test-id={`${testIdPrefix}_add`}
            data-cy={`${testIdPrefix}_add`}
          >
            <Icon path={mdiPlus} size="regular" />
          </Button>
          <Button
            variant="tertiary"
            disabled={isFormDisabled || isPinPending}
            aria-label="toggle pin"
            onClick={() => onPinGlobalMeasurementType(globalTypeId)}
            data-test-id={`${testIdPrefix}_pin`}
            data-cy={`${testIdPrefix}_pin`}
          >
            <Icon
              path={isPinned ? mdiPin : mdiPinOutline}
              size="regular"
              data-cy={`${testIdPrefix}_pin_icon_${isPinned ? 'filled' : 'outlined'}`}
            />
          </Button>
        </div>
      </div>

      {!hasAnyRows ? (
        <TextRegular
          className="text-sm text-foreground_faded"
          data-test-id={`${testIdPrefix}_no_mappings`}
        >
          {t('measurementsSettings.noMeasurementYet')}
        </TextRegular>
      ) : (
        <div className="flex flex-col gap-4">
          {sortedLimsMeasurementTypes.map((mapping, index) => (
            <MappingRow
              key={mapping.mappingId}
              index={index}
              mapping={mapping}
              availableLimsTypes={unmappedLimsMeasurementTypes}
              units={units}
              isDisabled={isFormDisabled}
              onLimsTypeChange={handleSelectLimsType}
              onUnitChange={handleSelectLimsUnit}
              onDelete={handleDeleteMappingRow}
              testIdPrefix={testIdPrefix}
            />
          ))}
          {unsavedRows.map((unsavedRow, index) => (
            <UnsavedMappingRow
              key={unsavedRow.tempId}
              index={sortedLimsMeasurementTypes.length + index}
              availableLimsTypes={unmappedLimsMeasurementTypes}
              units={units}
              isDisabled={isFormDisabled}
              onLimsTypeChange={(limsTypeId) =>
                handleSelectTypeInUnsavedMappingRow(unsavedRow.tempId, limsTypeId)
              }
              onDelete={() => handleDeleteUnsavedMappingRow(unsavedRow.tempId)}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
      )}
    </div>
  )
}
